import {
  CellClickedEvent,
  GridReadyEvent,
  type IRowNode,
  type ModelUpdatedEvent,
  ModuleRegistry,
  type SelectionChangedEvent,
} from 'ag-grid-community'
import { AllEnterpriseModule } from 'ag-grid-enterprise'
import { Loader2Icon } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import { useSignalTypeTitles } from '@/api/signals.hook'
import { Alert } from '@/gen/inventory/v1/alert_pb'

import { collectVisibleAgGridRowIds } from '@/lib/ag-grid/collect-visible-ag-grid-row-ids'
import { useDrawerNavigationStore } from '@/lib/app/drawer-navigation.hook'
import { cn } from '@/lib/ui/style-helpers'

import { AgDataTable } from '@/components/lemmala-design-system/table'

import { getCompanyDrawerAlertColumnDefs } from './config/column-defs'

// Register AG Grid modules
ModuleRegistry.registerModules([AllEnterpriseModule])

export type AgAlertTableProps = {
  dataSource: Alert[]
  onClick?: (id: string) => void
  disableDefaultFilter?: boolean
  readOnly?: boolean
  loading?: boolean
  showFilterBar?: boolean
  className?: string
  containerClassName: string
  filterOnlyOpenAndOngoing?: boolean
  selection?: {
    enabled: boolean
    multiple?: boolean
    selectOnClick?: boolean
    initialSelectedIds?: string[]
    onSelectionChanged?: (ids: string[]) => void
  }
}

export const AgAlertTable = ({
  dataSource,
  onClick,
  disableDefaultFilter,
  loading = false,
  showFilterBar,
  className,
  containerClassName,
  filterOnlyOpenAndOngoing = false,
  selection,
  readOnly,
}: AgAlertTableProps) => {
  const syncDrawerNavigationIds = useDrawerNavigationStore()
  const [quickFilterText, setQuickFilterText] = useState('')

  const allScopeValues = useMemo(() => {
    return [] as string[]
  }, [])

  const titleByType = useSignalTypeTitles()

  const columnDefs = useMemo(
    () =>
      getCompanyDrawerAlertColumnDefs({
        rows: dataSource,
        disableDefaultFilter,
        allScopeValues,
        readOnly,
        titleByType,
      }),
    [dataSource, disableDefaultFilter, allScopeValues, readOnly, titleByType],
  )

  const handleGridReady = useCallback(
    (e: GridReadyEvent<Alert>) => {
      const state = e.api.getColumnState()
      const hasSort = state?.some((c) => c.sort)
      if (!hasSort) {
        e.api.applyColumnState({
          // Default to severity asc if no sort chosen
          state: [{ colId: 'severity', sort: 'asc' }],
        })
      }

      const hasFilters = e.api.getColumnFilterModel('status')
      if (!hasFilters && filterOnlyOpenAndOngoing) {
        e.api.setColumnFilterModel('status', {
          filterType: 'set',
          values: ['Open', 'Ongoing'],
        })
        e.api.onFilterChanged() // commit filter changes to the table
      }

      if (selection?.enabled && selection.initialSelectedIds) {
        const selectedNodes: IRowNode<Alert>[] = []
        for (const id of selection.initialSelectedIds) {
          const node = e.api.getRowNode(id)
          if (node) {
            selectedNodes.push(node)
          }
        }
        e.api.setNodesSelected({
          nodes: selectedNodes,
          newValue: true,
        })
      }
    },
    [filterOnlyOpenAndOngoing, selection],
  )

  const onCellClicked = useCallback(
    (params: CellClickedEvent<Alert>) => {
      const id = params.data?.id
      if (id && onClick && params.column.getColId() !== 'status') {
        onClick(id)
      }
    },
    [onClick],
  )

  const onSelectionChanged = useCallback(
    (e: SelectionChangedEvent<Alert>) => {
      if (!selection?.onSelectionChanged) {
        return
      }

      selection.onSelectionChanged(e.api.getSelectedRows().map((row: Alert) => row.id))
    },
    [selection],
  )

  const memoGridOptions = useMemo(
    () => ({
      onCellClicked,
      onSelectionChanged,
      onModelUpdated: (params: ModelUpdatedEvent<Alert>) => {
        if (!params) return
        syncDrawerNavigationIds(collectVisibleAgGridRowIds(params.api))
      },
    }),
    [onCellClicked, onSelectionChanged, syncDrawerNavigationIds],
  )

  if (loading) {
    return (
      <div className='flex h-96 items-center justify-center'>
        <Loader2Icon className='size-8 animate-spin text-main-foreground-tertiary' />
      </div>
    )
  }

  return (
    <div className={cn('-mx-2', className)}>
      <AgDataTable<Alert>
        domLayout='normal'
        rowData={dataSource}
        columnDefs={columnDefs}
        gridOptions={memoGridOptions}
        onGridInit={handleGridReady}
        urlSync={{ enabled: false }} // Disable URL sync for drawer context
        filters={{
          defaultVisibleFilters: ['severity', 'alert', 'status', 'dateDetected'],
        }}
        search={{
          minQueryLength: 2,
          value: quickFilterText,
          onChange: setQuickFilterText,
        }}
        selection={{
          mode: selection?.multiple ? 'multiple' : 'singleRow',
          checkboxes: selection?.enabled ?? false,
          selectOnClick: selection?.selectOnClick ?? false,
        }}
        hideHeader={!showFilterBar}
        hideExport
        entityName='alert'
        containerClassName={containerClassName}
      />
    </div>
  )
}
